// The whole router. Four paths do not need a library.
//
// It exists because sign-in redirects the browser to app.<domain>/owner or
// /owner/create, and until now every path mounted the same screen: an owner who
// finished signing in landed on the till's pairing screen.
//
// Cloudflare Pages serves index.html for a path it does not recognise, which is
// what makes a client-side path work at all. Without that, /owner would be a
// CDN 404 before this code ever ran.

import { loadOnNavigate } from './build.svelte';

const inBrowser = typeof window !== 'undefined';

let path = $state(inBrowser ? window.location.pathname : '/');

if (inBrowser) {
	// The back button changes the URL without reloading, so the path has to be
	// read again rather than assumed to be whatever navigate() last set.
	window.addEventListener('popstate', () => {
		path = window.location.pathname;
	});
}

/** The current path, without a trailing slash except at the root. */
export function currentPath(): string {
	return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

/**
 * Move to a path inside this app, without reloading the bundle.
 *
 * Unless Pages now serves a newer bundle (INT-7): then the navigation is the
 * dashboard's and the admin's safe moment, and `to` loads as a new page.
 */
export function navigate(to: string): void {
	if (!inBrowser || to === path) return;
	if (loadOnNavigate(to)) return;
	window.history.pushState({}, '', to);
	path = to;
}

/** Replace the current path, leaving no entry to go back to.
 *
 * Used after sign-in: the server sends the browser to /owner/create, and an
 * owner who then creates their restaurant must not be able to press back into
 * a form for a restaurant that now exists.
 */
export function replacePath(to: string): void {
	if (!inBrowser || to === path) return;
	window.history.replaceState({}, '', to);
	path = to;
}

/** The app's page a mailed link opens (D18): the server builds the link to it. */
export const EMAIL_LINK_PATH = '/auth/verify';

/** Where an owner creates an account with an email (D17). */
export const SIGN_UP_PATH = '/owner/signup';

/** The app's page a reset link opens (D18, UC-26): the server builds the link to it. */
export const RESET_LINK_PATH = '/auth/reset';

/** Where an owner asks for a reset link (D19). */
export const FORGOT_PATH = '/owner/forgot';

/** An owner's ways in (D20), under Settings. Google's link flow comes back here. */
export const SIGN_IN_METHODS_PATH = '/owner/sign-in-methods';

/**
 * Whether the current path is inside the owner dashboard. A mailed link's
 * page is the owner's too: it ends with the owner signed in.
 */
export function isOwnerPath(): boolean {
	const p = currentPath();
	return (
		p === '/owner' || p.startsWith('/owner/') || p === EMAIL_LINK_PATH || p === RESET_LINK_PATH
	);
}
